import { useEffect, useRef, useCallback } from 'react'

/**
 * Shared throttled ResizeObserver hook.
 * Combines RAF + debounce to prevent layout thrashing during CSS transitions.
 *
 * @param ref - Ref to the element to observe
 * @param onResize - Callback fired with (width, height) after throttle
 * @param debounceMs - Post-RAF debounce delay (default 80ms)
 */
export function useThrottledResize(
  target: React.RefObject<HTMLElement | null> | HTMLElement | null,
  onResize: (width: number, height: number) => void,
  debounceMs = 80,
) {
  const onResizeRef = useRef(onResize)
  onResizeRef.current = onResize

  const cleanupRef = useRef<{
    ro: ResizeObserver
    rafId: number | null
    timeoutId: ReturnType<typeof setTimeout> | null
    lastW: number
    lastH: number
  } | null>(null)

  const getElement = useCallback((): HTMLElement | null => {
    if (!target) return null
    if (typeof (target as React.RefObject<HTMLElement | null>).current !== 'undefined') {
      return (target as React.RefObject<HTMLElement | null>).current
    }
    return target as HTMLElement
  }, [target])

  const observe = useCallback(() => {
    const el = getElement()
    if (!el) return

    let rafId: number | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let lastW = 0
    let lastH = 0

    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (!rect) return
      const w = rect.width
      const h = rect.height
      if (w === lastW && h === lastH) return

      if (rafId) cancelAnimationFrame(rafId)
      if (timeoutId) clearTimeout(timeoutId)

      rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          lastW = w
          lastH = h
          onResizeRef.current(w, h)
        }, debounceMs)
      })
    })

    ro.observe(el)
    cleanupRef.current = { ro, rafId, timeoutId, lastW, lastH }

    return () => {
      ro.disconnect()
      if (rafId) cancelAnimationFrame(rafId)
      if (timeoutId) clearTimeout(timeoutId)
      cleanupRef.current = null
    }
  }, [getElement, debounceMs])

  useEffect(() => {
    const cleanup = observe()
    return cleanup
  }, [observe])
}
